<template>
  <div class="content">
    <div class="md-layout">
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <h1>Admin management</h1>

        <md-button
          class="md-primary"
          @click="createUserDialog.active = true"
        >
          Create user
        </md-button>

        <form class="row">
          <md-field>
            <md-select
              v-model="filters.role"
              placeholder="Filter by role"
              @md-selected="filterByRole"
            >
              <md-option
                v-for="role in adminRoles"
                :key="role"
                :value="role"
              >
                {{ adminRoleToString[role] }}
              </md-option>
            </md-select>
          </md-field>

          <md-field>
            <label>Search by email</label>
            <md-input v-model="filters.email" />
          </md-field>
          <md-button @click="filterByEmail">
            Search
          </md-button>
        </form>

        <md-progress-bar
          v-if="isLoading"
          md-mode="query"
        />
        <v-client-table
          v-else
          :data="admins"
          :columns="table.columns"
          :options="table.options"
        >
          <template #email="props">
            <button
              class="email"
              @click="onUserClick(props.row)"
            >
              {{ props.row.email }}
            </button>
          </template>

          <template #role="props">
            {{ adminRoleToString[props.row.role] }}
          </template>

          <template #created_at="props">
            {{ props.row.created_at && (new Date(props.row.created_at)).toUTCString() }}
          </template>

          <template #updated_at="props">
            {{ props.row.updated_at && (new Date(props.row.updated_at)).toUTCString() }}
          </template>
        </v-client-table>

        <md-dialog :md-active.sync="createUserDialog.active">
          <md-dialog-title>Create user</md-dialog-title>
          <md-dialog-content>
            <form>
              <md-field :class="{ 'md-invalid': $v.createUserDialog.form.email.$error }">
                <label>Email</label>
                <md-input
                  v-model="createUserDialog.form.email"
                  type="email"
                />
                <span class="md-error">Must be a valid email</span>
              </md-field>

              <md-field :class="{ 'md-invalid': $v.createUserDialog.form.name.$error }">
                <label>Name</label>
                <md-input v-model="createUserDialog.form.name" />
                <span class="md-error">Field is required</span>
              </md-field>

              <md-field :class="{ 'md-invalid': $v.createUserDialog.form.password.$error }">
                <label>Password</label>
                <md-input
                  v-model="createUserDialog.form.password"
                  type="password"
                />
                <span class="md-error">Field is required, at least 8 characters</span>
              </md-field>

              <md-field :class="{ 'md-invalid': $v.createUserDialog.form.role.$error }">
                <label>Role</label>
                <md-select v-model="createUserDialog.form.role">
                  <md-option
                    v-for="role in selectableAdminRoles"
                    :key="role"
                    :value="role"
                  >
                    {{ adminRoleToString[role] }}
                  </md-option>
                </md-select>
                <span class="md-error">Field is required</span>
              </md-field>
            </form>
          </md-dialog-content>
          <md-dialog-actions>
            <md-button @click="createUserDialog.active = false">
              Cancel
            </md-button>
            <md-button
              class="md-primary"
              @click="createUser"
            >
              Create
            </md-button>
          </md-dialog-actions>
        </md-dialog>

        <md-dialog :md-active.sync="userDetailsDialog.active">
          <md-dialog-title>User details</md-dialog-title>
          <md-dialog-content>
            <static-info-field
              label="Email"
              :value="userDetailsDialog.selectedUser.email"
            />

            <static-info-field
              label="Name"
              :value="userDetailsDialog.selectedUser.full_name"
            />

            <div
              class="changeRoleForm"
            >
              <md-field>
                <label>Role</label>
                <md-select
                  v-model="userDetailsDialog.selectedUser.role"
                  placeholder="Role"
                  @md-selected="filterByRole"
                >
                  <md-option
                    v-for="role in adminRoles"
                    :key="role"
                    :value="role"
                  >
                    {{ adminRoleToString[role] }}
                  </md-option>
                </md-select>
              </md-field>

              <md-button
                class="md-primary"
                @click="onRoleChange"
              >
                Update role
              </md-button>
            </div>

            <static-info-field
              label="Created at"
              :value="userDetailsDialog.selectedUser.created_at && (new Date(userDetailsDialog.selectedUser.created_at)).toUTCString()"
            />

            <static-info-field
              label="Updated at"
              :value="userDetailsDialog.selectedUser.updated_at && (new Date(userDetailsDialog.selectedUser.updated_at)).toUTCString()"
            />
          </md-dialog-content>
          <md-dialog-actions>
            <div class="userDetailsActions">
              <md-button
                class="md-primary deleteButton"
                @click="onDeleteUserClick"
              >
                Delete
              </md-button>

              <md-button @click="userDetailsDialog.active = false">
                Close
              </md-button>
            </div>
          </md-dialog-actions>
        </md-dialog>

        <md-dialog-confirm
          :md-active.sync="deleteUserDialog.active"
          md-title="Delete user?"
          md-confirm-text="Delete"
          md-cancel-text="Cancel"
          @md-confirm="onUserDelete"
        />
      </div>
    </div>
  </div>
</template>
<script>
    import {adminRoles, selectableAdminRoles, adminRoleToString} from "../assets/adminRoles";
    import {Event} from "vue-tables-2-premium";
    import {required, email, minLength} from "vuelidate/lib/validators";
    import StaticInfoField from "../components/UserDetails/StaticInfoField";

    export default {
        name: "AdminManagement",
        components: {
          StaticInfoField
        },
        data() {
            return {
              adminRoles,
              adminRoleToString,
              selectableAdminRoles,

              isLoading: true,
              admins: [],
              nextPageToken: "",

              createUserDialog: {
                active: false,
                form: {
                  email: "",
                  name: "",
                  password: "",
                  role: ""
                }
              },

              userDetailsDialog: {
                active: false,
                selectedUser: {}
              },

              deleteUserDialog: {
                active: false
              },

              filters: {
                role: "",
                email: ""
              },

              table: {
                columns: [
                  "email", "full_name", "role", "created_at", "updated_at"
                ],
                options: {
                  perPage: 200,
                  customFilters: [
                    {
                      name: "role",
                      callback(row, query) {
                        if (!query) {
                          return true;
                        }

                        return row.role === query;
                      }
                    },
                    {
                      name: "email",
                      callback(row, query) {
                        if (!query) {
                          return true;
                        }

                        return row.email.includes(query);
                      }
                    }
                  ]
                }
              }
            };
        },
        mounted() {
          this.fetchAccounts();
        },
        methods: {
          fetchAccounts() {
            this.isLoading = true;
            this.$http.get("/v1/admin/list-users")
              .then(res => { this.admins = res.data.admins; })
              .then(() => { this.isLoading = false; });
          },
          filterByRole() {
            Event.$emit("vue-tables.filter::role", this.filters.role);
          },
          filterByEmail() {
            Event.$emit("vue-tables.filter::email", this.filters.email);
          },
          createUser () {
            this.$v.createUserDialog.form.$touch();

            if (this.$v.createUserDialog.form.$error) {
              return;
            }

            this.$http.post("/v1/admin/create-user", {
              email: this.createUserDialog.form.email,
              full_name: this.createUserDialog.form.name,
              password: this.createUserDialog.form.password,
              role: this.createUserDialog.form.role,
            }).then(() => {
              this.$notify({
                group: "foo",
                title: "User created",
              });
              window.location.reload();
            }).catch(err => {
              this.$notify({
                group: "foo",
                title: `Error with creating user: ${err}`,
              });
            });
          },
          onUserClick (user) {
            this.userDetailsDialog.selectedUser = user;
            this.userDetailsDialog.active = true;
          },
          onRoleChange () {
            this.$http.post("/v1/admin/update-role", {
              id: this.userDetailsDialog.selectedUser.id,
              role: this.userDetailsDialog.selectedUser.role
            }).then(() => {
              this.$notify({
                group: "foo",
                title: "User role update",
              });
            }).catch(err => {
              this.$notify({
                group: "foo",
                title: `Error with updating user role: ${err}`,
              });
            });
          },
          onDeleteUserClick () {
            this.deleteUserDialog.active = true;
          },
          onUserDelete () {
            this.$http.post("/v1/admin/delete-user", {
              id: this.userDetailsDialog.selectedUser.id
            }).then(() => {
              this.$notify({
                group: "foo",
                title: "User deleted",
              });
              window.location.reload();
            }).catch(err => {
              this.$notify({
                group: "foo",
                title: `Error with deleting user: ${err}`,
              });
            });
          },
        },
        validations: {
          createUserDialog: {
            form: {
              email: { required, email },
              name: { required },
              password: { required, minLength: minLength(8) },
              role: { required }
            }
          }
        }
    };
</script>

<style lang="scss">
  .searchForm {
    display: flex;
    column-gap: 16px;

    .searchButton {
      height: 40px;
    }
  }

  .email {
    display: block;
    text-align: left;
    padding: 8px;

    background: none;
    border: none;
    cursor: pointer;

    color: #00b0ff;
  }

  .VuePagination {
    justify-content: center;
  }

  .loadMoreButton {
    display: block;
    margin: 12px auto;
  }

  .changeRoleForm {
    display: flex;
    padding: 0 8px;
    column-gap: 12px;

    > div {
      max-width: 300px;
    }
  }

  .userDetailsActions {
    width: 100%;
    display: flex;
    justify-content: space-between;

    .deleteButton {
      background: #ad1457 !important;
    }
  }
</style>
